/**
 * Source of truth: which KP ids currently have a playable lesson.
 *
 * Kept in sync with `src/learn/lesson-route-table.ts` (`KP_ROUTE`) by
 * `tests/learn/lesson-route-table.test.ts`. To wire a new KP, add it to
 * BOTH this set AND `KP_ROUTE` — the test will fail loudly if they drift.
 *
 * The chapter-page UI consults `isLessonWired(kpId)` to grey out KP cards
 * that don't have a runner yet.
 */
export const WIRED_LESSON_KPS = new Set<string>([
  // ── math (42) — 7 legacy wrappers + 35 via math-lesson.ts dispatcher ──
  'math.mul.2x1',
  'math.mul.2x2',
  'math.div.remainder',
  'math.word.two-step',
  'math.mul.3x1',
  'math.div.no-remainder',
  'math.div.mul-relation',
  // generator-backed (task #41 Phase B)
  'math.calc.addsub-vertical',
  'math.number.thousand',
  'math.fraction.compare',
  'math.decimal.basic',
  'math.decimal.addsub',
  'math.area.rectangle',
  'math.time.duration',
  'math.time.clock',
  'math.number.10000',
  'math.place.value.4digits',
  'math.add.carry.multi',
  'math.sub.borrow.multi',
  'math.mul.2x1.review',
  'math.div.1digit.no-rem',
  'math.time.elapsed',
  'math.large.number.4up',
  'math.add.sub.multi.4up',
  'math.mul.2x2.4up',
  'math.mul.3x1.4up',
  'math.word.two-step.4up',
  'math.frac.add.same-denom.4up',
  'math.decimal.2place.4up',
  'math.time.complex.4up',
  // bank-backed (task #41 Phase B)
  'math.fraction.basic',
  'math.length.cm',
  'math.weight.kg',
  'math.angle.basic',
  'math.shape.rectangle',
  'math.shape.circle',
  'math.stats.bar',
  'math.stats.table',
  'math.frac.unit',
  'math.volume.basic.4up',
  'math.stats.bar.4up',
  // stage6 wire: meow911 段考真題池 — #250 coverage-fix:6 個小 bank 合併進 misc-pool
  'math.meow911.misc',

  // ── chinese (19) ───────────────────────────────────────────────────
  'chinese.zhuyin.basic',
  'chinese.phonetic.tone',
  'chinese.vocab.usage',
  'chinese.character.radical',
  'chinese.segment.summary',
  'chinese.sentence.pattern',
  'chinese.comprehension.basic',
  'chinese.comprehension.explain',
  'chinese.writing.guided',
  'chinese.writing.diary',
  'chinese.punctuation.basic',
  'chinese.rhetoric.basic',
  'chinese.idiom.basic',
  'chinese.culture.community',
  'chinese.phonetic.tone.up',
  'chinese.vocab.daily.up',
  'chinese.sentence.simple.up',
  'chinese.comprehension.short.up',
  'chinese.writing.fillin.up',
  // 三下 by-lesson banks (spec #76)
  'chinese.l01.xuyuan',
  'chinese.l02.xiayushihou',
  'chinese.l03.yujianmeiruunainai',
  'chinese.l04.gongjiangzhizu',
  'chinese.l05.xuetianshukai',
  'chinese.l06.shenqimima',
  'chinese.l07.youtonghua',
  // 三下 by-lesson banks L08-L12 (spec #82)
  'chinese.l08.daziranmeishuguan',
  'chinese.l09.taiwandeshanjiaoyu',
  'chinese.l10.yufuhejinyu',
  'chinese.l11.congmingdeshulu',
  'chinese.l12.haiyaodiejici',
  // stage6 wire: meow911 段考真題池 — #250 coverage-fix:8 個小 bank 合併進 misc-pool
  'chinese.character.radical.meow911',
  'chinese.sentence.pattern.meow911',
  'chinese.meow911.misc',

  // ── english (17) ───────────────────────────────────────────────────
  'english.alphabet.phonics',
  'english.phonics.basic',
  'english.vocab.family',
  'english.vocab.school',
  'english.vocab.food',
  'english.vocab.animals',
  'english.vocab.colors',
  'english.numbers.basic',
  'english.numbers.ordinal',
  'english.grammar.questions',
  'english.grammar.statements',
  'english.dialogue.daily',
  'english.dialogue.classroom',
  'english.alphabet.review.up',
  'english.vocab.school.up',
  'english.vocab.colors.up',
  'english.greetings.intro.up',
  // stage6 wire: meow911 段考真題池 — #250 coverage-fix:4 個小 bank 合併進 misc-pool
  'english.grammar.questions.meow911',
  'english.meow911.misc',

  // ── nature (18) ────────────────────────────────────────────────────
  'nature.plant.lifecycle',
  'nature.plant.growth',
  'nature.animal.classify',
  'nature.animal.adapt',
  'nature.water.states',
  'nature.water.temp',
  'nature.weather.seasons',
  'nature.weather.measure',
  'nature.magnet.basic',
  'nature.machine.basic',
  'nature.force.deform',
  'nature.sound.light.basic',
  'nature.light.basic',
  'nature.rock.basic',
  'nature.observation.basic.up',
  'nature.water.air.up',
  'nature.kitchen.science.up',
  'nature.light.shadow.up',
  // stage6 wire: meow911 段考真題池 — #250 coverage-fix:6 個小 bank 合併進 misc-pool
  'nature.plant.lifecycle.meow911',
  'nature.water.states.meow911',
  'nature.meow911.misc',

  // ── social (12) ────────────────────────────────────────────────────
  'social.community.basic',
  'social.family.roles',
  'social.taiwan.geography',
  'social.local.environment',
  'social.hometown.basic',
  'social.custom.festival',
  'social.life.rule',
  'social.traffic.basic',
  'social.environment.basic',
  'social.self.family.up',
  'social.school.life.up',
  'social.time.place.up',
  'social.rules.basic.up',
  // stage6 wire: meow911 段考真題池 — #250 coverage-fix:1 個小 bank (ch4) 合併進 misc-pool
  'social.community.basic.meow911',
  'social.taiwan.geography.meow911',
  'social.hometown.basic.meow911',
  'social.traffic.basic.meow911',
  'social.environment.basic.meow911',
  'social.meow911.misc',
]);

export function isLessonWired(kpId: string): boolean {
  return WIRED_LESSON_KPS.has(kpId);
}
